'use client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from '@/store/auth.store';
import Header from '@/components/layout/Header';
import CookieBanner from '@/components/ui/CookieBanner';
import BottomNavWrapper from '@/components/layout/BottomNavWrapper';
import TelegramMiniApp from '@/components/TelegramMiniApp';
import AttributionCapture from '@/components/analytics/AttributionCapture';

export default function Providers({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        retry: 1,
        staleTime: 30000,
        refetchOnWindowFocus: false,
      },
    },
  }));
  const initAuth = useAuthStore(state => state.initAuth);
  const isInitialized = useAuthStore(state => state.isInitialized);
  const user = useAuthStore(state => state.user);

  useEffect(() => {
    initAuth();
  }, [initAuth]);

  const showAuthOverlay = !isInitialized && !user;

  return (
    <QueryClientProvider client={queryClient}>
      {showAuthOverlay && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-bg-subtle">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand border-t-transparent" />
        </div>
      )}
      <Toaster
        position="top-right"
        containerStyle={{
          top: 16,
          right: 16,
          left: 16,
        }}
        toastOptions={{
          duration: 3000,
          style: {
            borderRadius: '12px',
            fontSize: '14px',
            maxWidth: 'min(420px, calc(100vw - 32px))',
            wordBreak: 'break-word',
          },
          success: { style: { background: '#f0fdf4', border: '1px solid #86efac', color: '#166534' } },
          error: { style: { background: '#fef2f2', border: '1px solid #fca5a5', color: '#991b1b' } },
        }}
      />
      <AttributionCapture />
      <TelegramMiniApp />
      <Header />
      <main className="min-h-screen bg-bg-subtle">
        {children}
      </main>
      <BottomNavWrapper />
      <CookieBanner />
    </QueryClientProvider>
  );
}
